import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type NyReise, type Reiselinje } from '../../api/konsoll';
import type { Sprak } from '../../api/reise';
import { SPRAK } from '../../i18n';

const EPOST = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const datoTid = (iso: string | null, sprak?: string) =>
  iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(sprak, { dateStyle: 'short', timeStyle: 'short' }) : '–';

/** Reisene: opprette en ny (lenken vises én gang og kan kopieres), åpne og trekke tilbake. */
export default function ReiseListe() {
  const { t, i18n } = useTranslation();
  const [reiser, setReiser] = useState<Reiselinje[] | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [bekreftTrekk, setBekreftTrekk] = useState<string | null>(null);

  const hent = useCallback(() => {
    konsollApi
      .reiser()
      .then(setReiser)
      .catch((e) => setFeil(e instanceof ApiFeil ? e.kode : 'ukjent'));
  }, []);

  useEffect(hent, [hent]);

  async function trekkTilbake(id: string) {
    setFeil(null);
    try {
      await konsollApi.trekkTilbake(id);
      setBekreftTrekk(null);
      hent();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <NyReiseSkjema onOpprettet={hent} />

      <section aria-labelledby="reiser-tittel">
        <h2 id="reiser-tittel" className="mb-3 text-xl font-semibold">
          {t('konsoll.reiser.tittel')}
        </h2>
        {feil && (
          <p role="alert" className="mb-3 melding-feil">
            {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
          </p>
        )}
        {reiser?.length === 0 && <p className="text-slate-700">{t('konsoll.reiser.ingen')}</p>}
        <ul className="flex flex-col gap-2">
          {reiser?.map((r) => (
            <li key={r.id} className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Link
                  to={`reiser/${r.id}`}
                  aria-label={t('konsoll.reiser.apne', { kunde: r.kundenavn })}
                  className="font-semibold text-slate-900 underline-offset-2 hover:underline"
                >
                  {r.kundenavn}
                </Link>
                <p className="text-sm text-slate-600">
                  <span className="mr-2 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800">{t(`konsoll.tilstand.${r.tilstand}`)}</span>
                  {t('konsoll.reiser.opprettet')}: {datoTid(r.opprettet, i18n.resolvedLanguage)} · {t('konsoll.reiser.sistAktiv')}:{' '}
                  {datoTid(r.sistAktiv, i18n.resolvedLanguage)}
                </p>
              </div>
              {r.tilstand !== 'trukket_tilbake' &&
                (bekreftTrekk === r.id ? (
                  <div className="flex flex-col gap-2 sm:items-end">
                    <p className="text-sm text-red-900">{t('konsoll.reiser.bekreftTrekk', { kunde: r.kundenavn })}</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => trekkTilbake(r.id)} className="knapp-primaer bg-red-800 hover:bg-red-700">
                        {t('konsoll.reiser.ja')}
                      </button>
                      <button type="button" onClick={() => setBekreftTrekk(null)} className="knapp-sekundaer">
                        {t('konsoll.reiser.avbryt')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setBekreftTrekk(r.id)} className="knapp-sekundaer shrink-0">
                    {t('konsoll.reiser.trekkTilbake')}
                  </button>
                ))}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function NyReiseSkjema({ onOpprettet }: { onOpprettet: () => void }) {
  const { t } = useTranslation();
  const tom: NyReise = { kundenavn: '', sprak: 'nb', modus: 'alene', kontaktNavn: '', kontaktEpost: '', salgReferanse: '', sendInvitasjon: true };
  const [ny, setNy] = useState<NyReise>(tom);
  const [lenke, setLenke] = useState<string | null>(null);
  const [sendtTil, setSendtTil] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);

  const sett = (felt: keyof NyReise) => (e: { target: { value: string } }) => setNy({ ...ny, [felt]: e.target.value });

  async function opprett(event: FormEvent) {
    event.preventDefault();
    if (!ny.kundenavn.trim() || !ny.kontaktNavn.trim() || !EPOST.test(ny.kontaktEpost.trim())) {
      setFeil('konsoll.ny.skrivAlt');
      return;
    }
    setOpptatt(true);
    setFeil(null);
    try {
      const resultat = await konsollApi.opprett({
        ...ny,
        kundenavn: ny.kundenavn.trim(),
        kontaktNavn: ny.kontaktNavn.trim(),
        kontaktEpost: ny.kontaktEpost.trim(),
        salgReferanse: ny.salgReferanse?.trim() || undefined,
      });
      setLenke(resultat.lenke);
      setSendtTil(resultat.invitasjonSendt ? ny.kontaktEpost.trim() : null);
      setKopiert(false);
      setNy(tom);
      onOpprettet();
    } catch (e) {
      setFeil(`feil.${e instanceof ApiFeil ? e.kode : 'ukjent'}`);
    } finally {
      setOpptatt(false);
    }
  }

  async function kopier() {
    if (!lenke) return;
    try {
      await navigator.clipboard.writeText(lenke);
      setKopiert(true);
    } catch {
      // Utklippstavlen er ikke tilgjengelig. Lenken står i feltet, og kan markeres og kopieres for hånd
    }
  }

  return (
    <section aria-labelledby="ny-reise-tittel" className="kort p-6 sm:p-8">
      <h2 id="ny-reise-tittel" className="mb-4 text-xl font-semibold">
        {t('konsoll.ny.tittel')}
      </h2>
      <form onSubmit={opprett} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Felt id="ny-kundenavn" etikett={t('konsoll.ny.kundenavn')} verdi={ny.kundenavn} onChange={sett('kundenavn')} />
          <Felt id="ny-salg" etikett={t('konsoll.ny.salgReferanse')} verdi={ny.salgReferanse ?? ''} onChange={sett('salgReferanse')} />
          <Felt id="ny-kontakt" etikett={t('konsoll.ny.kontaktNavn')} verdi={ny.kontaktNavn} onChange={sett('kontaktNavn')} />
          <Felt id="ny-epost" etikett={t('konsoll.ny.kontaktEpost')} verdi={ny.kontaktEpost} onChange={sett('kontaktEpost')} type="email" />
          <div className="flex flex-col">
            <label htmlFor="ny-sprak" className="mb-1.5 text-sm font-medium text-slate-700">
              {t('konsoll.ny.sprak')}
            </label>
            <select id="ny-sprak" value={ny.sprak} onChange={(e) => setNy({ ...ny, sprak: e.target.value as Sprak })} className="felt">
              {SPRAK.map((s) => (
                <option key={s} value={s}>
                  {t(`sprak.${s}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label htmlFor="ny-modus" className="mb-1.5 text-sm font-medium text-slate-700">
              {t('konsoll.ny.modus')}
            </label>
            <select id="ny-modus" value={ny.modus} onChange={(e) => setNy({ ...ny, modus: e.target.value as NyReise['modus'] })} className="felt">
              <option value="alene">{t('konsoll.ny.alene')}</option>
              <option value="med_konsulent">{t('konsoll.ny.med_konsulent')}</option>
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={ny.sendInvitasjon ?? false}
            onChange={(e) => setNy({ ...ny, sendInvitasjon: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-aksent-600 focus:ring-aksent-600"
          />
          {t('konsoll.ny.sendInvitasjon')}
        </label>
        {feil && (
          <p role="alert" className="melding-feil">
            {t(feil, { defaultValue: t('feil.ukjent') })}
          </p>
        )}
        <div>
          <button type="submit" disabled={opptatt} className="knapp-primaer">
            {t('konsoll.ny.opprett')}
          </button>
        </div>
      </form>

      {lenke && (
        <div className="mt-4 melding-ok">
          <label htmlFor="ny-lenke" className="mb-1 block font-medium text-aksent-900">
            {t('konsoll.ny.lenke')}
          </label>
          {sendtTil && <p className="mb-2 text-sm font-medium text-aksent-900">{t('konsoll.ny.sendt', { epost: sendtTil })}</p>}
          <p className="mb-2 text-sm text-aksent-900">{t('konsoll.ny.lenkeHjelp')}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input id="ny-lenke" readOnly value={lenke} onFocus={(e) => e.target.select()} className="felt w-full font-mono text-sm" />
            <button type="button" onClick={kopier} className="knapp-sekundaer inline-flex shrink-0 items-center gap-2">
              <Copy aria-hidden="true" className="h-4 w-4" />
              {kopiert ? t('konsoll.ny.kopiert') : t('konsoll.ny.kopier')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Felt({ id, etikett, verdi, onChange, type = 'text' }: { id: string; etikett: string; verdi: string; onChange: (e: { target: { value: string } }) => void; type?: string }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="mb-1.5 text-sm font-medium text-slate-700">
        {etikett}
      </label>
      <input id={id} type={type} value={verdi} onChange={onChange} className="felt" />
    </div>
  );
}
